import MagnumSays from '@/components/MagnumSays'
import {
  ActCatalog,
  CatalogSection,
  SectionPace,
  formatClock,
  formatDuration,
  paceBudget,
  paceLevelOf,
  sectionBudget,
} from '@/lib/actModes'
import { cn } from '@/lib/utils'

/**
 * Magnus's read on where the pace ladder should start today, said out loud so
 * the target never looks arbitrary.
 */
function paceAdvice(section: CatalogSection, pace: SectionPace | null, budget: number): string {
  const testPace = section.secondsPerQuestion
  if (!pace || pace.avgSecondsPerQuestion == null || pace.sampleQuestions < 3) {
    return `The real thing gives you ${formatDuration(testPace)} a question. We're not starting there — take ${formatDuration(budget)} today and we'll tighten it as you go.`
  }
  const avg = pace.avgSecondsPerQuestion
  if (budget <= testPace) {
    return `You're averaging ${formatDuration(avg)} a question, and the test gives you ${formatDuration(testPace)}. You've earned the real clock — let's see it hold up for a whole section.`
  }
  return `You're averaging ${formatDuration(avg)} a question. Test pace is ${formatDuration(testPace)}, so today we're aiming at ${formatDuration(budget)}. Beat it and I'll tighten it next time.`
}

interface ActModePickerProps {
  catalog: ActCatalog | null
  section: CatalogSection
  sectionPace: SectionPace | null
  paceLevelKey: string | null
  onPaceLevel: (key: string) => void
  onPractice: (focusKey: string) => void
  onTest: () => void
  onBack: () => void
}

/**
 * The second and last question before he's working: what is this round about?
 * One question at a time under a single call, or the whole section against the
 * clock.
 */
export default function ActModePicker({
  catalog,
  section,
  sectionPace,
  paceLevelKey,
  onPaceLevel,
  onPractice,
  onTest,
  onBack,
}: ActModePickerProps) {
  const level = paceLevelOf(catalog, paceLevelKey)
  const budget = paceBudget(section, level)
  const total = sectionBudget(section, level)
  const recommended = sectionPace?.recommendedPaceLevel

  return (
    <div className="space-y-6">
      <button
        type="button"
        onClick={onBack}
        className="text-sm text-muted-foreground hover:text-foreground transition"
      >
        &larr; {section.emoji} {section.label}
      </button>

      <MagnumSays>What are we testing on these?</MagnumSays>

      <div className="space-y-3">
        {(catalog?.focuses || []).map(f => (
          <button
            key={f.key}
            type="button"
            onClick={() => onPractice(f.key)}
            className="w-full flex items-center gap-4 px-5 py-4 rounded-2xl border-2 border-border hover:border-primary hover:bg-accent transition text-left min-h-[44px]"
          >
            <span className="text-2xl shrink-0">{f.emoji}</span>
            <span className="flex-1">
              <span className="block text-lg font-bold">{f.label}</span>
              <span className="block text-xs text-muted-foreground">{f.blurb}</span>
            </span>
          </button>
        ))}
      </div>

      <div className="border-2 border-border rounded-2xl p-5 space-y-4">
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <h2 className="text-lg font-bold">🏁 Full section</h2>
          <p className="text-xs text-muted-foreground">
            real thing: {section.questions} questions · {section.minutes} min
          </p>
        </div>

        <MagnumSays size="sm">{paceAdvice(section, sectionPace, budget)}</MagnumSays>

        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Today's clock</p>
          <div className="flex flex-wrap gap-2">
            {(catalog?.paceLevels || []).map(p => (
              <button
                key={p.key}
                type="button"
                onClick={() => onPaceLevel(p.key)}
                className={cn(
                  'px-3 py-2 rounded-xl border-2 text-sm transition min-h-[44px]',
                  p.key === paceLevelKey
                    ? 'border-primary bg-primary/10 font-medium'
                    : 'border-border hover:border-primary/50 hover:bg-accent'
                )}
                aria-pressed={p.key === paceLevelKey}
              >
                {p.label}
                {p.key === recommended && <span className="ml-1 text-primary">★</span>}
              </button>
            ))}
          </div>
          {level && <p className="text-xs text-muted-foreground mt-2">{level.blurb}</p>}
        </div>

        <div className="grid grid-cols-2 gap-3 text-center">
          <div className="p-3 rounded-xl border border-border">
            <p className="text-2xl font-bold tabular-nums">{formatClock(total)}</p>
            <p className="text-xs text-muted-foreground">for the section</p>
          </div>
          <div className="p-3 rounded-xl border border-border">
            <p className="text-2xl font-bold tabular-nums">{formatDuration(budget)}</p>
            <p className="text-xs text-muted-foreground">a question</p>
          </div>
        </div>

        <button
          type="button"
          onClick={onTest}
          className="w-full px-6 py-4 bg-primary text-primary-foreground rounded-xl font-bold hover:opacity-90 transition min-h-[44px]"
        >
          Start the section
        </button>
      </div>
    </div>
  )
}
